// ============================================================
// The waiting rooms, kept in step.
//
// One document per room, optimistic locally and written through - the
// same posture `useBattlefieldSync` takes with tokens, and for the same
// reason: a rail that waits for a round trip before it redraws feels
// broken even when it is working.
//
// SOLO SESSIONS HAVE ROOMS TOO. Without a session there is nothing to
// sync to and the rooms live in memory, which is exactly what a solo
// player wants and costs one branch.
//
// A PLAYER WITHOUT THE GRANT SEES NOTHING, and that is enforced in
// `firestore.rules`, not here. The listener resolves a permission
// denial to an empty list so their rail is simply absent rather than
// broken - but if this hook were the only guard it would be theatre.
// ============================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { railOrder } from '../engine/roster/waitingRoom';

export default function useWaitingRooms({ sessionId, mp, canSee = true }) {
  const [rooms, setRooms] = useState([]);
  const roomsRef = useRef(rooms);
  roomsRef.current = rooms;

  useEffect(() => {
    if (!sessionId || !canSee) return undefined;
    const unsub = mp.onWaitingRoomsUpdate(sessionId, (incoming) => setRooms(railOrder(incoming)));
    return () => unsub();
  }, [sessionId, canSee, mp]);

  /** Write one room through, and show it at once. */
  const writeRoom = useCallback(
    (room) => {
      setRooms((prev) => railOrder([...prev.filter((r) => r.id !== room.id), room]));
      if (sessionId) mp.setWaitingRoom(sessionId, room).catch(console.error);
    },
    [sessionId, mp],
  );

  const removeRoom = useCallback(
    (roomId) => {
      setRooms((prev) => prev.filter((r) => r.id !== roomId));
      if (sessionId) mp.deleteWaitingRoom(sessionId, roomId).catch(console.error);
    },
    [sessionId, mp],
  );

  return { rooms, writeRoom, removeRoom, roomsRef };
}
